<script setup>
import { storeToRefs } from 'pinia'
import LocationPin from 'assets/icons/location-pin.svg'
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder.vue'
import { useMessageStore } from '~/stores/messages'

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

const props = defineProps({
    carouselTeaserItem: {
        type: Object,
        required: true,
        default: () => ({}),
        validator(value) {
            const validations = {
                title: value => value.title === undefined || typeof value.title === 'string',
                uid: value => value.uid === undefined || typeof value.uid === 'number',
                type: value => value.type === undefined || (typeof value.type === 'string' && ['news', 'event'].includes(value.type)),
                imageUrl: value => value.imageUrl === undefined || typeof value.imageUrl === 'string',
                location: value => value.location === undefined || typeof value.location === 'string',
                myceExclusive: value => value['myce-exclusive'] === undefined || typeof value['myce-exclusive'] === 'boolean',
                date: value => value.date === undefined || typeof value.date === 'string',
                placeholder: value => value?.placeholder === undefined || typeof value.placeholder === 'boolean'
            }

            return Object.entries(validations).every(([_, validation]) => validation(value))
        }
    }
})

const { $md } = useNuxtApp()
const carouselHeadline = computed(() => $md.renderInline(props.carouselTeaserItem?.title || ''))

const route = useRoute()

// Fetch Center Details
const centerRoute = computed(() => route.params?.centerUid)
const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

// Prefetch data on the server side
onServerPrefetch(async () => {
    await centerDetailSuspense() // Wait for the query to finish fetching
})

// Get carouselItemBadge
const carouselItemBadge = computed(() => {
    return props?.carouselTeaserItem['myce-exclusive']
        ? staticKeys.value?.myce_exclusive_tag_news_event
        : props?.carouselTeaserItem?.type === 'news'
            ? staticKeys.value?.content_type_news
            : staticKeys.value?.content_type_event
})

const localePath = useLocalePath()

// Get slider link
const sliderLink = computed(() => {
    const isTypeNews = props?.carouselTeaserItem?.type === 'news'
    const uid = props?.carouselTeaserItem?.uid

    // Use localePath properly by passing the route name and params
    return isTypeNews
        ? localePath({ name: 'centerUid-news-newsUid', params: { newsUid: uid } })
        : localePath({ name: 'centerUid-events-eventsUid', params: { eventsUid: uid } })
})

const plainTitle = computed(() => props.carouselTeaserItem?.title?.replace(/[*\n]/g, ''))

const trackItem = () => {
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.CONTENT_TEASER_CLICK,
        centerUid: centerDetails.value?.centerUid,
        centerName: centerDetails.value?.centerName,
        contentTitle: plainTitle.value,
        contentType: carouselItemBadge.value?.toLowerCase(),
        screenName: TRACKING_CONSTANTS.MISC.HOME_PAGE,
    })
}
const itemComponent = computed(() => props?.carouselTeaserItem?.placeholder ? 'DefaultContentBox' : resolveComponent('NuxtLinkLocale'))

const getCarouselTeaserItemClass = computed(() => {
    return {
        'skeleton-placeholder-container': props?.carouselTeaserItem?.placeholder
    }
})
</script>

<template>
    <component
        :is="itemComponent"
        :class="getCarouselTeaserItemClass"
        class="carousel-teaser-item"
        :to="sliderLink"
        @click="trackItem"
    >
        <div class="carousel-teaser-item-image-container">
            <component
                :is="SkeletonPlaceholder"
                v-if="carouselTeaserItem.placeholder"
                :skeleton-placeholder-config="{ type: 'image', width: '565px', height: '317px ' }"
            />

            <picture v-else-if="isTypeImage(carouselTeaserItem.imageUrl)">
                <RespImgSrc
                    tag-type="source"
                    :image-src="carouselTeaserItem.imageUrl"
                    :srcset="[246, 385, 420, 565]"
                    :format="['avif', 'webp', 'jpg']"
                    :width="565"
                    :height="317"
                    sizes="(min-width: 1440px) 385px, (min-width: 1025px) calc(28vw - 20px), (min-width: 768px) 54.95vw, calc(75vw - 32px)"
                />
                <RespImgSrc
                    tag-type="img"
                    :image-src="carouselTeaserItem.imageUrl"
                    :srcset="[206, 385, 421, 565]"
                    :format="['jpg']"
                    :width="565"
                    :height="317"
                    sizes="70vw"
                    :additional-attrs="{ loading: 'lazy', alt: plainTitle }"
                />
            </picture>
        </div>
        <div class="carousel-teaser-item-badge">
            <span>{{ carouselItemBadge }}</span>
        </div>
        <div class="carousel-teaser-item-content-container">
            <header class="carousel-teaser-item-header">
                <div class="carousel-teaser-item-headline-container">
                    <component
                        :is="SkeletonPlaceholder"
                        v-if="carouselTeaserItem.placeholder"
                        :skeleton-placeholder-config="{ type: 'multi-headline', tag: 'h3' }"
                    />
                    <template v-else>
                        <h3 v-html="carouselHeadline" />
                    </template>
                </div>

                <h4 v-if="carouselTeaserItem?.location && ! carouselTeaserItem.placeholder">
                    <LocationPin class="carousel-teaser-item-location-icon" />
                    <span>{{ carouselTeaserItem.location }}</span>
                </h4>
            </header>

            <div v-if="carouselTeaserItem?.dateText || carouselTeaserItem?.timeText" class="carousel-teaser-date">
                <component
                    :is="SkeletonPlaceholder"
                    v-if="carouselTeaserItem.placeholder"
                    :skeleton-placeholder-config="{ type: 'text' }"
                />

                <p v-else-if="carouselTeaserItem.dateText">
                    {{ carouselTeaserItem.dateText }}
                </p>

                <component
                    :is="SkeletonPlaceholder"
                    v-if="carouselTeaserItem.placeholder"
                    :skeleton-placeholder-config="{ type: 'text' }"
                />
                <p v-else-if="carouselTeaserItem.timeText && carouselTeaserItem.type === 'event'">
                    {{ carouselTeaserItem.timeText }}
                </p>
            </div>
        </div>
    </component>
</template>

<style lang="scss">
.carousel-teaser-item {
    display: grid;
    grid-template-rows: auto 17px 17px 1fr;
    height: 100%;
    overflow: hidden;
    cursor: pointer;
    color: inherit;
    text-decoration: none;
    border-radius: 10px;
    transform: scale(0.92);
    transition: transform 400ms ease;

    .swiper-slide-active & {
        transform: scale(1);
    }

    @include bp(medium1) {
        border-radius: 20px;
    }

    @include bp(large1) {
        transform: scale(1);
    }
}

.carousel-teaser-item-image-container {
    grid-row: 1 / 3;
    grid-column: 1 / 2;
    width: 100%;
    height: 100%;
    z-index: 1;
    overflow: hidden;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    aspect-ratio: 16 / 9;

    @include bp(medium1) {
        border-top-left-radius: 20px;
        border-top-right-radius: 20px;
    }

    img {
        display: block;
        width: 100%;
        aspect-ratio: 16 / 9;
        object-fit: cover;
        object-position: top;
        height: auto;
    }
}

.carousel-teaser-item-badge {
    grid-row: 1 / 2; // redesign (CN-12074): badge top-left of the image (was bottom)
    grid-column: 1 / 2;
    place-self: start;
    display: flex;
    align-items: center;
    height: px2rem(36);
    margin: px2rem(20);
    padding-inline: px2rem(16);
    border-radius: px2rem(100);
    color: $carousel-teaser-badge-text;
    background-color: $carousel-teaser-badge-bg;
    font-size: px2rem(12);
    font-weight: 500;
    line-height: px2rem(18);
    z-index: 2;
}

.carousel-teaser-item-content-container {
    grid-row: 3 / 5;
    grid-column: 1 / 2;
    padding: 20px 16px 24px; // redesign (CN-12074): less top space now that the badge moved off the content top
    color: $carousel-teaser-text;
    background: $carousel-teaser-bg;
    display: flex;
    flex-flow: column wrap;
    min-height: 190px;
    overflow: hidden;

    @include bp(medium1) {
        padding: 20px 24px 24px;
        min-height: 200px;
    }
}

.carousel-teaser-item-header {
    margin-block-end: px2rem(20);

    h4 {
        display: flex;
        align-items: center;
        gap: px2rem(8);
        font-size: px2rem(14);
        line-height: px2rem(20);
        font-weight: 600;
        margin-block: 0;
        color: $grey5-v2; // redesign (CN-12074): Figma location uses text-secondary

        em {
            font-style: normal;
        }
    }

    .carousel-teaser-item-location-icon {
        flex-shrink: 0;
        width: px2rem(12);
        height: px2rem(12);
        color: $grey1-v2; // Figma marker grey
    }
}

.carousel-teaser-item-headline-container {
    max-height: calc(2 * var(--line-height)); /* Adjust based on your font size and line height */
    overflow: hidden;

    h3 {
        font-size: px2rem(18);
        line-height: px2rem(26);
        font-weight: 500;
        margin-block: 0 px2rem(5);
        text-wrap: balance;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;

        em {
            font-style: normal;
        }
    }
}

.carousel-teaser-date {
    margin-top: auto;
    min-height: 40px;

    p {
        font-weight: 500;
        font-size: px2rem(14);
        margin: 0;
    }
}
</style>
